import React from 'react';
import { Navigate } from 'react-router-dom';

import { Account } from 'pages/account/edit';
import { AlertTypeListing } from 'pages/alert-type/listing';
import { LocationListing } from 'pages/locations/listing';
import { Location } from 'pages/locations/edit';
import { User, UserProfile } from 'pages/users/edit';
import { UserCreate } from 'pages/users/create';
import { LocationCreate } from 'pages/locations/create';
import { PolicyCreate } from 'pages/policy/create';
import { UploadDocumentPage } from 'pages/policy/document/upload';
import { TachoImport } from 'pages/import/tacho-files';
import { BulkImportHistory, BulkImportUpload, BulkImportDetail, SavedMappings, importUploadActions, importDetailActions } from 'pages/import/bulk-imports';
import { FileExtractListing, allScope, fileExtractAction, mineScope } from 'pages/file-extract';
import { Page404 } from 'pages/errors/page-404';
import { UserListing } from 'pages/users/listing';
import { ExternalContactListing } from 'pages/external-contacts/listing';
import { ExternalContactCreate } from 'pages/external-contacts/create';
import { ExternalContactEdit } from 'pages/external-contacts/edit';
import { Policies, Policy } from 'pages/policy';
import { PolicyListing } from 'pages/policy/listing';
import { HierarchiesListing } from 'pages/locations/hierarchies/listing';
import { HierarchyCreate } from 'pages/locations/hierarchies/create';
import { Hierarchy } from 'pages/locations/hierarchies/edit';
import { ScheduleListing } from 'pages/schedules/listing';
import { ScheduleCreate } from 'pages/schedules/create';
import { Schedule } from 'pages/schedules/edit';
import { SelfReportingAdminListing } from 'pages/internal-admin/self-service-reports/listing';
import { SelfReportingAdminCreate } from 'pages/internal-admin/self-service-reports/create';
import { SelfReportingAdminEdit } from 'pages/internal-admin/self-service-reports/edit';
import { SelfServiceReports } from 'pages/self-service-reports';
import { GlobalSearch } from 'pages/internal-admin/global-search';
import { ErroredSchedules } from 'pages/internal-admin/errored-schedules';
import { RulesEngineHarness } from 'pages/internal-admin/rules-engine/harness';
import { RulesEngineDiffViewer } from 'pages/internal-admin/rules-engine/diff-viewer';
import { JobReport } from 'pages/report';
import { OcrPrevisit } from 'pages/ocr/previsit';
import { OcrCompleteReview } from 'pages/ocr/complete-previsit';
import { ReviewListing } from 'pages/review/listing';
import { EmployeeReviews } from 'pages/employee-reviews';
import { EmployeeSettings } from 'pages/employee-settings/EmployeeSettings';
import { Dashboard } from 'pages/dashboards';
import { ScheduledReportsListing } from 'pages/scheduled-reports/listing';
import { ScheduledReport } from 'pages/scheduled-reports/view';
import { Employee } from 'pages/employee/edit';
import { AlertListing } from 'pages/alert-type/alert/listing';
import { Asset } from 'pages/asset/edit';
import { AssetCreate } from 'pages/asset/create';
import { ManageAssets } from 'pages/asset/manage';
import { WalkaroundCheckListing } from 'pages/walkaround-check/listing';
import { CompletedCheck } from 'pages/checks/check/CompletedCheck';
import { TyreChecksListing } from 'pages/tyre-checks/listing/TyreChecksListing';
import { ChecksListing } from 'pages/checks';
import { CheckSettings } from 'pages/checks/settings';
import { ChecksTemplateListing } from 'pages/checks/listing';
import { DefectReportingDetails } from 'pages/walkaround-check/details';
import { SystemMessageListing } from 'pages/internal-admin/system-messages/listing';
import { SystemMessageCreate } from 'pages/internal-admin/system-messages/create';
import { SystemMessageUpdate } from 'pages/internal-admin/system-messages/edit';
import { GreyFleetInsuranceListing } from 'pages/internal-admin/grey-fleet-insurance/listing';
import { GreyFleetInsuranceValidation } from 'pages/internal-admin/grey-fleet-insurance/validation';
import { SystemMonitoring } from 'pages/internal-admin/system-monitoring';
import { TachoFileViewer } from 'pages/tacho-file-viewer';
import { RiskSettings } from 'pages/risk/settings';
import { RiskOverview } from 'pages/risk/overview';
import { CollisionsListing } from 'pages/collisions/listing/CollisionsListing';
import { CollisionCreate } from 'pages/collisions/create';
import { CollisionEdit } from 'pages/collisions/edit';
import { JobListing, JobAmendmentListing, JobCompletedListing } from 'pages/job/listing';
import { VisitsListing } from 'pages/visits/listing';
import { Job } from 'pages/job/edit';
import { RiskEmployeeProfile, RiskEmployeeListing } from 'pages/risk/employee';
import { HomePage } from 'pages/home-page';
import { Vehicle } from 'pages/vehicle';
import { RiskAssetListing, RiskAssetProfile } from 'pages/risk/asset';
import { Calendar } from 'pages/vehicle/calendar';
import { CloseJobView } from 'pages/job/edit/close-job/View';
import { OperatingLicenceListing } from 'pages/operating-licences/listing';
import { OperatingLicenceView } from 'pages/operating-licences/view';
import { BetaFeatures } from 'pages/beta-features';
import { NewTemplate } from 'pages/checks/templates/create/NewTemplate';
import { LicenceCheckingSettings } from 'pages/licence-checking/settings/LicenceCheckingSettings';
import { LicenceChecking } from 'pages/licence-checking';
import { LicenceCheckingOnSiteDeclaration } from 'pages/licence-checking/declaration-on-site';
import { LicenceCheckingPolicyCreate } from 'pages/licence-checking/policies/create';
import { LicenceCheckingPolicyEdit } from 'pages/licence-checking/policies/edit';
import { RiskOrganisationProfile } from 'pages/risk/organisation';
import { EditTemplate } from 'pages/checks/templates/edit/EditTemplate';
import { TemplateBuilder } from 'pages/checks/templates/builder';
import { TemplateProvider } from 'pages/checks/context';
import { GreyFleet } from 'pages/licence-checking/grey-fleet';
import { GreyFleetSettings } from 'pages/licence-checking/grey-fleet/settings';
import { GreyFleetCreate } from 'pages/licence-checking/grey-fleet/create';
import { EventTypeEdit, FleetSettings } from 'pages/fleet/fleet-settings';
import { Fleet, FleetHub } from 'pages/fleet';
import { EventTypeWizard } from 'pages/fleet/fleet-event-type/wizard/EventTypeWizard';
import { GreyFleetCheck } from 'pages/licence-checking/grey-fleet/check';
import { DocumentSettings } from 'pages/documents/settings/DocumentSettings';
import { NewDocumentType } from 'pages/documents/settings/document-types/create/NewDocumentType';
import { EditDocumentType } from 'pages/documents/settings/document-types/edit/EditDocumentType';
import { FailedVisitView } from 'pages/tyre-checks/view';
import { SingleDocument } from 'pages/documents/settings/all-documents/single-document/SingleDocument';
import { LicenceCheckingCredits } from 'pages/licence-checking/credits';
import { PcnAdminListing } from 'pages/pcn/admin/listing';
import { PcnAdminEdit } from 'pages/pcn/admin/edit';
import { PcnSettings } from 'pages/pcn/admin/settings';
import { StorageOverviewListing } from 'pages/internal-admin/storage-overview/listing/StorageOverviewListing';
import { StorageOverview } from 'pages/internal-admin/storage-overview/listing/StorageOverview';
import { ManualEntryTemplateCreate } from 'pages/employee-settings/manual-entry-templates/create';
import { ManualEntryTemplateEdit } from 'pages/employee-settings/manual-entry-templates/edit';
import { ManualEntryTemplates } from 'pages/manual-entry-templates';
import { EarnedRecognition, DriverKpiDrilldown, MaintenanceKpiDrilldown } from 'pages/earned-recognition';
import { EmployeeAssessments } from 'pages/employee-assessments';
import { Reseller } from 'pages/reseller';
import { ResellerSettings } from 'pages/reseller/settings/ResellerSettings';
import { TyreChecksReconcile } from 'pages/tyre-checks/reconcile';

export const selfAdminDefaultActions = ["SelfAdmin:View", "Internal"];
export const ocrCompleteReviewActions = ["Internal:OCR", "ComplianceReview:View"];
export const alertActions = ['VISAlerts:View', 'TachoAlerts:View', 'Debrief:View', 'Training:View', 'DriverApp:Approve', 'HoldingArea:View', 'LicenceCheck:View', 'DriversHours:View', 'Employees:Import', 'Fleet:Import', 'PlannedMaintenance:Import', 'PrePlan:Import', 'Training:Import', 'Collisions:Import'];

let mainLayoutRoutes = null;

function createRoute({ path, action = null, breadcrumbs = null, title = null, render = null, renderAtLocation = null }) {
    let element;

    if (renderAtLocation != null) {
        element = (location) => renderAtLocation({ breadcrumbs, title, location });
    } else {
        element = render({ breadcrumbs, title });
    }

    return { path, action, breadcrumbs, title, element };
}

function buildMainLayoutRoutes() {
    return [
        createRoute({
            path: "/account/*",
            action: selfAdminDefaultActions,
            breadcrumbs: [{ location: "/account", title: "Account" }],
            render: ({ breadcrumbs }) => <Account breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/alert-type/*",
            action: alertActions,
            breadcrumbs: [{ location: "/alert-type", title: "Alert Types" }],
            render: ({ breadcrumbs }) => <AlertTypeListing breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/location",
            action: selfAdminDefaultActions,
            breadcrumbs: [{ location: "/location", title: "Location Management" }],
            render: ({ breadcrumbs }) => <LocationListing breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/location/create",
            action: selfAdminDefaultActions,
            breadcrumbs: [{ location: "/location", title: "Location Management" }, { title: "Add Location" }],
            render: ({ breadcrumbs }) => <LocationCreate breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/location/hierarchies",
            action: selfAdminDefaultActions,
            breadcrumbs: [{ location: "/location", title: "Location Management" }, { title: "Location Hierarchies" }],
            render: ({ breadcrumbs }) => <HierarchiesListing breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/location/hierarchies/create",
            action: selfAdminDefaultActions,
            breadcrumbs: [{ location: "/location", title: "Location Management" }, { location: "/location/hierarchies", title: "Location Hierarchies" }, { title: "Add Location Hierarchy" }],
            render: ({ breadcrumbs }) => <HierarchyCreate breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/location/hierarchies/:id/*",
            action: selfAdminDefaultActions,
            breadcrumbs: [{ location: "/location", title: "Location Management" }, { location: "/location/hierarchies", title: "Location Hierarchies" }, { title: "Edit Hierarchy" }],
            render: ({ breadcrumbs }) => <Hierarchy breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/location/:id/*",
            action: selfAdminDefaultActions,
            breadcrumbs: [{ location: "/location", title: "Location Management" }, { title: "Edit Location" }],
            render: ({ breadcrumbs }) => <Location breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/user",
            action: selfAdminDefaultActions,
            breadcrumbs: [{ location: "/user", title: "User Management" }],
            render: ({ breadcrumbs }) => <UserListing breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/external-contact",
            action: "Users:ExternalContacts:Manage",
            breadcrumbs: [{ location: "/external-contact", title: "External Contact Management" }],
            render: ({ breadcrumbs }) => <ExternalContactListing breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/external-contact/create",
            action: "Users:ExternalContacts:Manage",
            breadcrumbs: [{ location: "/external-contact", title: "External Contact  Management" }, { title: "Add External Contact" }],
            render: ({ breadcrumbs }) => <ExternalContactCreate breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/external-contact/:id",
            action: "Users:ExternalContacts:Manage",
            breadcrumbs: [{ location: "/external-contact", title: "External Contact  Management" }, { title: "Edit External Contact" }],
            render: ({ breadcrumbs }) => <ExternalContactEdit breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/user/create",
            action: selfAdminDefaultActions,
            breadcrumbs: [{ location: "/user", title: "User Management" }, { title: "Add User" }],
            render: ({ breadcrumbs }) => <UserCreate breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/user/profile/*",
            breadcrumbs: [{ title: "User Profile" }],
            render: ({ breadcrumbs }) => <UserProfile breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/user/:id/*",
            action: selfAdminDefaultActions,
            breadcrumbs: [{ location: "/user", title: "User  Management" }, { title: "Edit User" }],
            render: ({ breadcrumbs }) => <User breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/schedule",
            action: selfAdminDefaultActions,
            breadcrumbs: [{ location: "/schedule", title: "Schedules Management" }],
            render: ({ breadcrumbs }) => <ScheduleListing breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/schedule/create",
            action: selfAdminDefaultActions,
            breadcrumbs: [{ location: "/schedule", title: "Schedule Management" }, { title: "Add Schedule" }],
            render: ({ breadcrumbs }) => <ScheduleCreate breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/schedule/:id/*",
            action: selfAdminDefaultActions,
            breadcrumbs: [{ location: "/schedule", title: "Schedule Management" }, { title: "Edit Schedule" }],
            render: ({ breadcrumbs }) => <Schedule breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/self-service-reports/*",
            action: "SelfService:View",
            breadcrumbs: [{ location: "/self-service-reports", title: "Self Service Reports" }],
            render: ({ breadcrumbs }) => <SelfServiceReports breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/import/tacho-files",
            action: "DCVU:Import",
            breadcrumbs: [{ location: "/import/tacho-files", title: "Import" }, { title: "Tacho Files" }],
            render: ({ breadcrumbs }) => <TachoImport breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/import/bulk/new",
            action: importUploadActions,
            breadcrumbs: [{ location: "/import/bulk", title: "Bulk Imports" }, { title: "New Import" }],
            render: ({ breadcrumbs }) => <BulkImportUpload breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/import/bulk/mappings",
            action: importUploadActions,
            breadcrumbs: [{ title: "Bulk Imports" }],
            render: ({ breadcrumbs }) => <SavedMappings breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/import/bulk/:sourceKey/:id",
            action: importDetailActions,
            breadcrumbs: [{ location: "/import/bulk", title: "Bulk Imports" }],
            render: ({ breadcrumbs }) => <BulkImportDetail breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/import/bulk",
            action: importUploadActions,
            breadcrumbs: [{ title: "Bulk Imports" }],
            render: ({ breadcrumbs }) => <BulkImportHistory breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/file-extract/all",
            action: fileExtractAction,
            breadcrumbs: [{ title: "File Extract" }],
            render: ({ breadcrumbs }) => <FileExtractListing breadcrumbs={breadcrumbs} scope={allScope} />
        }),
        createRoute({
            path: "/file-extract",
            action: fileExtractAction,
            breadcrumbs: [{ title: "File Extract" }],
            render: ({ breadcrumbs }) => <FileExtractListing breadcrumbs={breadcrumbs} scope={mineScope} />
        }),
        createRoute({
            path: "/policy-management/policies",
            action: selfAdminDefaultActions,
            breadcrumbs: [{ location: "/home", title: "HOME" }, { title: "POLICIES" }],
            render: ({ breadcrumbs }) => <PolicyListing breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/employee-reviews/*",
            action: ["EmployeeReview:View", "EmployeeReview:Manage"],
            breadcrumbs: [{ location: "/home", title: "HOME" }, { title: "EMPLOYEE REVIEWS" }],
            render: ({ breadcrumbs }) => <EmployeeReviews breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/scheduled-reports/:id",
            action: "ScheduleReports:View",
            breadcrumbs: [{ location: "/scheduled-reports", title: "Scheduled Reports" }, { title: "View Reports" }],
            render: ({ breadcrumbs }) => <ScheduledReport breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/scheduled-reports",
            action: "ScheduleReports:View",
            breadcrumbs: [{ location: "/scheduled-reports", title: "Scheduled Reports" }],
            render: ({ breadcrumbs }) => <ScheduledReportsListing breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/policy-management/policies/create",
            action: selfAdminDefaultActions,
            breadcrumbs: [{ location: '/home', title: "HOME" }, { location: "/policy-management", title: "POLICIES" }, { title: "Add Policy" }],
            render: ({ breadcrumbs }) => <PolicyCreate breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/policy-management/policies/upload-document",
            action: selfAdminDefaultActions,
            breadcrumbs: [{ location: "/home", title: "HOME" }, { location: "/policy-management", title: "POLICIES" }, { location: "/policy-management/upload-document", title: "Upload Documents" }],
            render: ({ breadcrumbs }) => <UploadDocumentPage breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/policy-management/policies/:id/*",
            action: selfAdminDefaultActions,
            breadcrumbs: [{ location: '/home', title: "HOME" }, { location: "/policy-management", title: "POLICIES" }, { title: "Policy DETAILS" }],
            render: ({ breadcrumbs }) => <Policy breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/manual-entry-templates/:from/:id",
            action: "Employees:AddManualEntry",
            render: () => <ManualEntryTemplates />
        }),
        createRoute({
            path: "/manual-entry-templates/:from",
            action: "Employees:AddManualEntry",
            render: () => <ManualEntryTemplates />
        }),
        createRoute({
            path: "/manual-entry-templates",
            action: "Employees:AddManualEntry",
            render: () => <ManualEntryTemplates />
        }),
        createRoute({
            path: "/employee-settings/manual-entry-templates/create",
            action: selfAdminDefaultActions,
            breadcrumbs: [{ location: "/employee-settings", title: "Employee Settings" }, { title: "Create Manual Entry Template" }],
            render: ({ breadcrumbs }) => <ManualEntryTemplateCreate breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/employee-settings/manual-entry-templates/:templateKey/:id/*",
            action: "Employees:AddManualEntry",
            breadcrumbs: [{ location: "/employee-settings", title: "Employee Settings" }, { title: "Manual Entry Template Settings" }],
            render: ({ breadcrumbs }) => <ManualEntryTemplateEdit breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/employee-settings/*",
            action: selfAdminDefaultActions,
            breadcrumbs: [{ location: "/employee-settings", title: "Employee Settings" }],
            render: ({ breadcrumbs }) => <EmployeeSettings breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/internal-admin/global-search",
            action: "Internal",
            breadcrumbs: [{ title: "Global Search" }],
            render: ({ breadcrumbs }) => <GlobalSearch breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/internal-admin/errored-schedules",
            action: "Internal",
            breadcrumbs: [{ title: "Errored Schedules" }],
            render: ({ breadcrumbs }) => <ErroredSchedules breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/internal-admin/rules-engine/harness",
            action: ["Internal"],
            breadcrumbs: [{ title: "Rules Engine Test Harness" }],
            render: ({ breadcrumbs }) => <RulesEngineHarness breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/internal-admin/rules-engine/diff-viewer",
            action: ["Internal"],
            breadcrumbs: [{ title: "Rules Engine Diff Viewer" }],
            render: ({ breadcrumbs }) => <RulesEngineDiffViewer breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/report/:id",
            action: "EmployeeAssessment:View",
            render: () => <JobReport />
        }),
        createRoute({
            path: "/internal-admin/self-service-reports/create",
            action: "Internal",
            breadcrumbs: [{ location: "/internal-admin/self-service-reports", title: "Self Reporting Admin" }, { title: "Create Report" }],
            render: ({ breadcrumbs }) => <SelfReportingAdminCreate breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/internal-admin/self-service-reports/:id",
            action: "Internal",
            breadcrumbs: [{ location: "/internal-admin/self-service-reports", title: "Self Reporting Admin" }, { title: "Edit Report" }],
            render: ({ breadcrumbs }) => <SelfReportingAdminEdit breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/internal-admin/self-service-reports",
            action: "Internal",
            breadcrumbs: [{ location: "/internal-admin/self-service-reports", title: "Self Reporting Admin" }],
            render: ({ breadcrumbs }) => <SelfReportingAdminListing breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/internal-admin/system-messages",
            action: "Internal",
            breadcrumbs: [{ location: "/internal-admin/system-messages", title: "System Message Management" }],
            render: ({ breadcrumbs }) => <SystemMessageListing breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/internal-admin/system-messages/create",
            action: "Internal",
            breadcrumbs: [{ location: "/internal-admin/system-messages", title: "System Message Management" }, { title: "Create System Message" }],
            render: ({ breadcrumbs }) => <SystemMessageCreate breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/internal-admin/system-messages/:id",
            action: "Internal",
            breadcrumbs: [{ location: "/internal-admin/system-messages", title: "System Message Management" }, { title: "Edit System Message" }],
            render: ({ breadcrumbs }) => <SystemMessageUpdate breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/internal-admin/grey-fleet-insurance",
            action: "Internal:GreyFleetInsurance",
            breadcrumbs: [{ location: "/", title: "HOME" }, { title: "Grey Fleet Insurance Validation" }],
            title: "Grey Fleet Insurance Validation",
            render: ({ breadcrumbs, title }) => <GreyFleetInsuranceListing breadcrumbs={breadcrumbs} title={title} requiredAction="Internal:GreyFleetInsurance" />
        }),
        createRoute({
            path: "/internal-admin/grey-fleet-insurance/:id",
            action: "Internal:GreyFleetInsurance",
            breadcrumbs: [{ location: "/", title: "HOME" }, { location: "/internal-admin/grey-fleet-insurance", title: "Grey Fleet Insurance Validation" }, { title: "Insurance Validation" }],
            title: "Insurance Validation",
            render: ({ breadcrumbs, title }) => <GreyFleetInsuranceValidation breadcrumbs={breadcrumbs} title={title} requiredAction="Internal:GreyFleetInsurance" />
        }),
        createRoute({
            path: "/system-monitoring",
            action: "Internal:DevTeam",
            breadcrumbs: [{ location: "/", title: "HOME" }, { title: "System Monitoring" }],
            render: ({ breadcrumbs }) => <SystemMonitoring breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/pcn-admin/on-hold/:id",
            action: "Internal",
            breadcrumbs: [{ location: "/pcn-admin", title: "PCN Admin" }, { location: "/pcn-admin/on-hold", title: "Holding Area" }, { title: "View On Hold PCN" }],
            render: ({ breadcrumbs }) => <PcnAdminEdit breadcrumbs={breadcrumbs} onHold />
        }),
        createRoute({
            path: "/pcn-admin/settings/*",
            action: "Internal",
            breadcrumbs: [{ location: "/pcn-admin", title: "PCN Admin" }, { title: "Settings" }],
            render: ({ breadcrumbs }) => <PcnSettings breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/pcn-admin/*",
            action: "Internal",
            breadcrumbs: [{ title: "PCN Admin" }],
            render: ({ breadcrumbs }) => <PcnAdminListing breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/upcoming-visits",
            action: "ScheduledAppointment:View",
            title: "Upcoming Visits",
            render: ({ title }) => <VisitsListing title={title} requiredAction="ScheduledAppointment:View" />
        }),
        createRoute({
            path: "/job-scheduling",
            action: "Internal",
            title: "Job Scheduling",
            render: ({ title }) => <JobAmendmentListing title={title} requiredAction="Internal" />
        }),
        createRoute({
            path: "/completed-visits/:id",
            action: "AuditsInspections:View",
            breadcrumbs: [{ location: "/completed-visits", title: "Completed visits" }, { title: "Completed visit" }],
            render: ({ breadcrumbs }) => <CloseJobView breadcrumbs={breadcrumbs} baseUrl="/completed-visits-view" />
        }),
        createRoute({
            path: "/completed-visits",
            action: "AuditsInspections:View",
            title: "Completed Visits",
            render: ({ title }) => <JobCompletedListing title={title} requiredAction="AuditsInspections:View" />
        }),
        createRoute({
            path: "/job-administration",
            action: "Internal",
            title: "Job Administration",
            render: ({ title }) => <JobListing title={title} administrationMode={true} requiredAction="Internal" />
        }),
        createRoute({
            path: "/job/scheduling-view/:jobId/*",
            action: "Internal:Jobscheduler",
            breadcrumbs: [{ location: "/job-scheduling", title: "job scheduling" }, { title: "Job" }],
            render: ({ breadcrumbs }) => <Job breadcrumbs={breadcrumbs} baseUrl="/scheduling-view" />
        }),
        createRoute({
            path: "/job/:jobId/*",
            action: "Internal",
            breadcrumbs: [{ location: "/job-administration", title: "job administration" }, { title: "Job" }],
            render: ({ breadcrumbs }) => <Job breadcrumbs={breadcrumbs} baseUrl="" />
        }),
        createRoute({
            path: "/reviews/compliance-review/pre-visit/:jobId",
            action: "Internal:OCR",
            breadcrumbs: [{ location: "/reviews", title: "Compliance Review" }, { title: "Pre-visit Settings" }],
            render: ({ breadcrumbs }) => <OcrPrevisit breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/reviews/compliance-review/:jobId",
            action: ocrCompleteReviewActions,
            breadcrumbs: [{ location: "/reviews", title: "Compliance Review" }, { title: "Complete" }],
            render: ({ breadcrumbs }) => <OcrCompleteReview breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/reviews",
            action: ocrCompleteReviewActions,
            breadcrumbs: [{ location: "/reviews", title: "Reviews" }, { title: "Review List" }],
            render: ({ breadcrumbs }) => <ReviewListing breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/alert/:alertTypeKey",
            breadcrumbs: [{ location: "/alert-type", title: "Alert Types" }, { title: "Alert List" }],
            render: ({ breadcrumbs }) => <AlertListing breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/earned-recognition",
            action: "ER:View",
            render: () => <EarnedRecognition />
        }),
        createRoute({
            path: "/earned-recognition/driver-kpi",
            action: "ER:View",
            breadcrumbs: [{ location: "/earned-recognition", title: "Earned Recognition" }, { title: "KPI Detail" }],
            render: ({ breadcrumbs }) => <DriverKpiDrilldown breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/earned-recognition/maintenance-kpi",
            action: "ER:View",
            breadcrumbs: [{ location: "/earned-recognition", title: "Earned Recognition" }, { title: "KPI Detail" }],
            render: ({ breadcrumbs }) => <MaintenanceKpiDrilldown breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/dashboards/:id",
            render: () => <Dashboard />
        }),
        createRoute({
            path: "/employee/:id/*",
            action: ["Employees:View", "AuthorisedDriver"],
            breadcrumbs: [{ location: `${window.env.VISION_URL}/#/Drivers/`, title: "Manage My Employees", externalLink: true }, { title: "Profile" }],
            render: ({ breadcrumbs }) => <Employee breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/asset/create/business-area/:businessArea",
            action: ["Fleet:Add", "FleetPCN:Add", "FleetVIS:Add"],
            breadcrumbs: [{ location: '/vehicle/manage', title: "Tacho Overview" }, { title: "Add Asset" }],
            render: ({ breadcrumbs }) => <AssetCreate breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/asset/create",
            action: ["Fleet:Add", "FleetPCN:Add", "FleetVIS:Add", "FMS:Edit"],
            breadcrumbs: [{ location: '/vehicle/manage', title: "Tacho Overview" }, { title: "Add Asset" }],
            render: ({ breadcrumbs }) => <AssetCreate breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/asset/manage",
            action: ["Fleet:View", "FleetPCN:View", "FleetVIS:View", "FMS:View"],
            breadcrumbs: [{ location: "/home", title: "HOME" }, { title: "MANAGE ASSETS" }],
            render: ({ breadcrumbs }) => <ManageAssets breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/asset/:id/*",
            action: ["Fleet:View", "FleetPCN:View", "FleetVIS:View"],
            breadcrumbs: [{ location: '/', title: "Home" }, { location: '/vehicle/manage', title: "Tacho Overview" }, { title: "Profile" }],
            render: ({ breadcrumbs }) => <Asset breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/walkaround-checks/:id",
            action: ["WalkaroundCheck:View"],
            breadcrumbs: [{ location: "", title: "" }, { title: "Walkaround check" }],
            render: ({ breadcrumbs }) => <DefectReportingDetails breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/tyre-checks/:id",
            action: ["Fleet:View", "FleetPCN:View", "FleetVIS:View"],
            breadcrumbs: [{ location: '/', title: "Home" }, { location: '/completed-visits', title: "Completed Visits" }, { title: "Tyre Checks" }],
            render: ({ breadcrumbs }) => <TyreChecksListing breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/tyre-checks-failed-visit/:id",
            action: ["Fleet:View", "FleetPCN:View", "FleetVIS:View"],
            breadcrumbs: [{ location: '/', title: "Home" }, { location: '/completed-visits', title: "Completed Visits" }, { title: "Tyre Checks" }],
            render: ({ breadcrumbs }) => <FailedVisitView breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/walkaround-checks",
            action: ["WalkaroundCheck:View"],
            breadcrumbs: [{ title: "Walkaround checks - Legacy" }],
            render: ({ breadcrumbs }) => <WalkaroundCheckListing breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/checks/templates/new",
            action: ["Checks:TemplateBuilder"],
            breadcrumbs: [{ location: "/checks/templates", title: "Checks Templates" }, { title: "New Template" }],
            renderAtLocation: ({ breadcrumbs, location }) => <NewTemplate key={location.pathname} breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/checks/standard-templates/new",
            action: "Internal",
            breadcrumbs: [{ location: "/checks/standard-templates", title: "Checks Standard Templates" }, { title: "New Standard Template" }],
            renderAtLocation: ({ breadcrumbs, location }) => <NewTemplate key={location.pathname} useGlobalConfig={true} breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/checks/templates/:id/settings/*",
            action: ["Checks:TemplateBuilder"],
            breadcrumbs: [{ location: "/checks/templates", title: "Checks Templates" }, { title: "Template Settings" }],
            renderAtLocation: ({ breadcrumbs, location }) => <EditTemplate key={location.pathname} breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/checks/standard-templates/:id/settings/*",
            action: "Internal",
            breadcrumbs: [{ location: "/checks/standard-templates", title: "Checks Standard Templates" }, { title: "Standard Template Settings" }],
            renderAtLocation: ({ breadcrumbs, location }) => <EditTemplate key={location.pathname} useGlobalConfig={true} breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/checks/templates/:id/*",
            action: ["Checks:TemplateBuilder"],
            breadcrumbs: [{ location: "/checks/templates", title: "Checks Templates" }, { title: "Template Builder" }],
            renderAtLocation: ({ breadcrumbs, location }) => <TemplateProvider><TemplateBuilder key={location.pathname} breadcrumbs={breadcrumbs} /></TemplateProvider>
        }),
        createRoute({
            path: "/checks/standard-templates/:id/*",
            action: "Internal",
            breadcrumbs: [{ location: "/checks/standard-templates", title: "Checks Standard Templates" }, { title: "Standard Template Builder" }],
            renderAtLocation: ({ breadcrumbs, location }) => <TemplateProvider><TemplateBuilder key={location.pathname} useGlobalConfig={true} breadcrumbs={breadcrumbs} /></TemplateProvider>
        }),
        createRoute({
            path: "/checks/templates",
            action: ["Checks:TemplateBuilder"],
            breadcrumbs: [{ location: "/home", title: "HOME" }, { location: "/checks", title: "Completed Checks" }, { title: "Checks Templates" }],
            renderAtLocation: ({ breadcrumbs, location }) => <ChecksTemplateListing key={location.pathname} breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/checks/standard-templates",
            action: "Internal",
            breadcrumbs: [{ title: "Checks Standard Templates" }],
            renderAtLocation: ({ breadcrumbs, location }) => <ChecksTemplateListing key={location.pathname} useGlobalConfig={true} breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/checks/settings/*",
            action: ["Checks:Manage"],
            breadcrumbs: [{ location: "/home", title: "HOME" }, { location: "/checks", title: "Completed Checks" }, { location: "/checks/settings", title: "Check Settings" }],
            render: ({ breadcrumbs }) => <CheckSettings breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/checks/:id/*",
            action: ["Checks:View"],
            breadcrumbs: [{ location: "", title: "" }, { title: "Check" }],
            render: ({ breadcrumbs }) => <CompletedCheck breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/checks",
            action: ["Checks:View"],
            breadcrumbs: [{ location: "/home", title: "HOME" }, { title: "Completed Checks" }],
            render: ({ breadcrumbs }) => <ChecksListing breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/tacho-file-viewer",
            action: ["Internal"],
            breadcrumbs: [{ title: "Tacho File Viewer" }],
            render: ({ breadcrumbs }) => <TachoFileViewer breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/risk-settings/*",
            action: ["Risk:Manage"],
            render: () => <RiskSettings />
        }),
        createRoute({
            path: "/risk/employee",
            action: ["Risk:View"],
            breadcrumbs: [{ location: "/risk", title: "Manage my Risk" }, { title: "Employee Risk" }],
            render: ({ breadcrumbs }) => <RiskEmployeeListing breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/risk/asset",
            action: ["Risk:View"],
            breadcrumbs: [{ location: "/risk", title: "Manage my Risk" }, { title: "Asset Risk" }],
            render: ({ breadcrumbs }) => <RiskAssetListing breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/risk/employee/:id",
            action: ["Risk:View"],
            breadcrumbs: [{ location: "/risk", title: "Manage my Risk" }, { location: "/risk/employee", title: "Employee Risk" }, { title: "Employee Profile" }],
            render: ({ breadcrumbs }) => <RiskEmployeeProfile breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/risk/asset/:id",
            action: ["Risk:View"],
            breadcrumbs: [{ location: "/risk", title: "Manage my Risk" }, { location: "/risk/asset", title: "Asset Risk" }, { title: "Asset Profile" }],
            render: ({ breadcrumbs }) => <RiskAssetProfile breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/risk/organisation",
            action: ["Risk:View"],
            breadcrumbs: [{ location: "/risk", title: "Manage my Risk" }, { title: "Organisation Profile" }],
            render: ({ breadcrumbs }) => <RiskOrganisationProfile breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/risk",
            action: ["Risk:View"],
            breadcrumbs: [{ title: "Manage my Risk" }],
            render: ({ breadcrumbs }) => <RiskOverview breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/operating-licences/:id/*",
            action: ["OperatingLicence:View"],
            breadcrumbs: [{ location: "/operating-licences?redirect=0", title: "Operating Licences" }, { title: "View Licence" }],
            render: ({ breadcrumbs }) => <OperatingLicenceView breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/operating-licences",
            action: ["OperatingLicence:View"],
            breadcrumbs: [{ location: "/home", title: "HOME" }, { title: "OPERATING LICENCES" }],
            render: ({ breadcrumbs }) => <OperatingLicenceListing breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/collisions/:id",
            action: ["Collisions:Import"],
            breadcrumbs: [{ location: "/collisions", title: "Collisions" }, { title: "Edit Collision" }],
            render: ({ breadcrumbs }) => <CollisionEdit breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/collisions/create",
            action: ["Collisions:Import"],
            breadcrumbs: [{ location: "/collisions", title: "Collisions" }, { title: "Add Collision" }],
            render: ({ breadcrumbs }) => <CollisionCreate breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/collisions",
            action: ["Collisions:View"],
            breadcrumbs: [{ title: "Collisions" }],
            render: ({ breadcrumbs }) => <CollisionsListing breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/vehicle/manage/:id/Calendar",
            action: ["Fleet:View", "FleetPCN:View", "FleetVIS:View"],
            breadcrumbs: [{ location: "/vehicle/manage", title: "Tacho Overview" }, { title: "Calendar" }],
            render: ({ breadcrumbs }) => <Calendar breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/vehicle/manage",
            action: ["Fleet:View"],
            render: () => <Vehicle />
        }),
        createRoute({
            path: "/fleet-settings/event-types/create",
            action: ["FMS:Manage"],
            breadcrumbs: [{ location: "/fleet-settings", title: "Fleet Settings" }, { location: "/fleet-settings/event-types", title: "Event Types" }, { title: "Create Event Type" }],
            render: ({ breadcrumbs }) => <EventTypeWizard breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/fleet-settings/event-types/:id/*",
            action: ["FMS:Manage"],
            breadcrumbs: [{ location: "/fleet-settings", title: "Fleet Settings" }, { location: "/fleet-settings/event-types", title: "Event Types" }],
            render: ({ breadcrumbs }) => <EventTypeEdit breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/fleet-settings/*",
            action: ["Fleet:ChangeAssetType", "FMS:Manage"],
            breadcrumbs: [{ location: "/fleet-settings", title: "Fleet Settings" }],
            render: ({ breadcrumbs }) => <FleetSettings breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/beta-features",
            render: () => <BetaFeatures />
        }),
        createRoute({
            path: "/Home",
            breadcrumbs: [{ title: "Home" }],
            render: ({ breadcrumbs }) => <HomePage breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/licence-checking/policies/:id/*",
            action: "LicenceChecking:Manage",
            breadcrumbs: [{ location: "/home", title: "HOME" }, { location: "/licence-checking", title: "LICENCE CHECKING HUB" }, { location: "/licence-checking/policies", title: "LICENCE CHECKING POLICIES" }, { title: "Edit Licence Checking Policy" }],
            render: ({ breadcrumbs }) => <LicenceCheckingPolicyEdit breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/licence-checking/policies/create",
            action: "LicenceChecking:Manage",
            breadcrumbs: [{ location: "/home", title: "HOME" }, { location: "/licence-checking", title: "LICENCE CHECKING HUB" }, { location: "/licence-checking/policies", title: "LICENCE CHECKING POLICIES" }, { title: "New Licence Checking Policy" }],
            render: ({ breadcrumbs }) => <LicenceCheckingPolicyCreate breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/licence-checking/settings/*",
            action: "LicenceChecking:Manage",
            breadcrumbs: [{ location: "/home", title: "HOME" }, { location: "/licence-checking", title: "LICENCE CHECKING HUB" }, { title: "MANAGE LICENCE CHECKING" }],
            render: ({ breadcrumbs }) => <LicenceCheckingSettings breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/licence-checking/credits/*",
            action: ["LicenceChecking:Manage", "LicenceChecking:View", "LicenceChecking:GreyFleet"],
            render: () => <LicenceCheckingCredits />
        }),
        createRoute({
            path: "/licence-checking/grey-fleet/check/:checkId",
            action: "LicenceChecking:GreyFleet",
            breadcrumbs: [{ location: "/home", title: "HOME" }, { location: "/licence-checking", title: "LICENCE CHECKING HUB" }, { location: "/licence-checking/grey-fleet/checks", title: "GREY FLEET" }, { title: "Grey Fleet Check" }],
            render: ({ breadcrumbs }) => <GreyFleetCheck breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/licence-checking/grey-fleet/settings/*",
            action: "LicenceChecking:GreyFleet",
            breadcrumbs: [{ location: "/home", title: "HOME" }, { location: "/licence-checking/grey-fleet", title: "GREY FLEET" }, { title: "Settings" }],
            render: ({ breadcrumbs }) => <GreyFleetSettings breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/licence-checking/grey-fleet/create",
            action: "LicenceChecking:GreyFleet",
            breadcrumbs: [{ location: "/home", title: "HOME" }, { location: "/licence-checking", title: "LICENCE CHECKING HUB" }, { location: "/licence-checking/grey-fleet", title: "GREY FLEET" }, { title: "Create New Grey Fleet Check" }],
            render: ({ breadcrumbs }) => <GreyFleetCreate breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/licence-checking/grey-fleet/*",
            action: "LicenceChecking:GreyFleet",
            breadcrumbs: [{ location: "/home", title: "HOME" }, { location: "/licence-checking", title: "LICENCE CHECKING HUB" }, { title: "GREY FLEET" }],
            render: ({ breadcrumbs }) => <GreyFleet breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/licence-checking/declaration/on-site/:employeeId",
            action: "LicenceChecking:View",
            render: () => <LicenceCheckingOnSiteDeclaration />
        }),
        createRoute({
            path: "/licence-checking/*",
            action: "LicenceChecking:View",
            breadcrumbs: [{ location: "/home", title: "HOME" }, { title: "LICENCE CHECKING" }],
            render: ({ breadcrumbs }) => <LicenceChecking breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/document-settings/types/new",
            action: "Documents:Manage",
            breadcrumbs: [{ location: "/home", title: "HOME" }, { title: "DOCUMENTS", location: `/document-settings/types` }, { title: "Add Document Type" }],
            render: ({ breadcrumbs }) => <NewDocumentType breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/document-settings/types/:id/*",
            action: "Documents:Manage",
            breadcrumbs: [{ location: "/home", title: "HOME" }, { title: "DOCUMENTS", location: `/document-settings/types` }, { title: "Edit Document Type" }],
            render: ({ breadcrumbs }) => <EditDocumentType breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/document-settings/documents/:id",
            action: "Documents:View",
            breadcrumbs: [{ location: "/home", title: "HOME" }],
            render: ({ breadcrumbs }) => <SingleDocument breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/document-settings/*",
            action: "Documents:Manage",
            breadcrumbs: [{ location: "/home", title: "HOME" }, { title: "DOCUMENTS" }],
            render: ({ breadcrumbs }) => <DocumentSettings breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/policy-management/*",
            action: "Documents:Manage",
            breadcrumbs: [{ location: "/home", title: "HOME" }, { title: "POLICIES" }],
            render: ({ breadcrumbs }) => <Policies breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/storage-overview/:id",
            action: "Internal",
            breadcrumbs: [{ location: "/home", title: "HOME" }, { title: "STORAGE OVERVIEW", location: "/storage-overview" }, { title: "STORAGE" }],
            render: ({ breadcrumbs }) => <StorageOverview breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/storage-overview",
            action: "Internal",
            breadcrumbs: [{ location: "/home", title: "HOME" }, { title: "STORAGE OVERVIEW" }],
            render: ({ breadcrumbs }) => <StorageOverviewListing breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/fleet/manage",
            action: "FMS:View",
            breadcrumbs: [{ location: "/home", title: "HOME" }, { title: "FLEET" }],
            render: ({ breadcrumbs }) => <Fleet breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/fleet/*",
            action: "FMS:View",
            breadcrumbs: [{ location: "/home", title: "HOME" }, { title: "FLEET OVERVIEW" }],
            render: ({ breadcrumbs }) => <FleetHub breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/internal-admin/tire-checks-reconcile",
            action: "Internal",
            breadcrumbs: [{ title: "Tyre Checks Reconcile" }],
            render: ({ breadcrumbs }) => <TyreChecksReconcile breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/driver-assessments/*",
            action: "EmployeeAssessments:View",
            breadcrumbs: [{ location: "/home", title: "HOME" }, { title: "DRIVER ASSESSMENTS" }],
            render: ({ breadcrumbs }) => <EmployeeAssessments breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/reseller/settings/*",
            action: "Reseller:Manage",
            breadcrumbs: [{ location: "/home", title: "HOME" }, { location: "/reseller", title: "RESELLER" }, { title: "RESELLER SETTINGS" }],
            render: ({ breadcrumbs }) => <ResellerSettings breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/reseller/*",
            action: "Reseller:Manage",
            breadcrumbs: [{ location: "/home", title: "HOME" }, { title: "RESELLER" }],
            render: ({ breadcrumbs }) => <Reseller breadcrumbs={breadcrumbs} />
        }),
        createRoute({
            path: "/",
            render: () => <Navigate to="/Home" replace />
        }),
        createRoute({
            path: "*",
            render: () => <Page404 />
        })
    ];
}

export function getMainLayoutRoutes() {
    if (mainLayoutRoutes == null) {
        mainLayoutRoutes = buildMainLayoutRoutes();
    }

    return mainLayoutRoutes;
}
